
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ConfigUpdateRequest} from './ConfigUpdateRequest';
import {FieldModelConfigUpdateRequest} from './ConfigUpdateRequest';
import {SecuritySettingsDeleteResponse} from './SecuritySettingsDeleteResponse';

/**
 * SecuritySettingsDeleteRequest struct
 */
class SecuritySettingsDeleteRequest extends ConfigUpdateRequest {
  /**
   * Initialize struct
   * @param {!ConfigUpdateRequest=} parent
   * @param {UUID=} securitySettingsId
   * @param {string=} securitySettingsName
   * @constructor
   */
  constructor (parent = new ConfigUpdateRequest(), securitySettingsId = null, securitySettingsName = null) {
    super()
    super.copy(parent)
    this.SecuritySettingsId = securitySettingsId
    this.SecuritySettingsName = securitySettingsName
  }

  /**
   * Copy struct (shallow copy)
   * @this {!SecuritySettingsDeleteRequest}
   * @param {!SecuritySettingsDeleteRequest} other Other struct
   * @returns {!SecuritySettingsDeleteRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.SecuritySettingsId != null) {
      this.SecuritySettingsId = new UUID(other.SecuritySettingsId)
    } else {
      this.SecuritySettingsId = null
    }
    if (other.SecuritySettingsName != null) {
      this.SecuritySettingsName = other.SecuritySettingsName
    } else {
      this.SecuritySettingsName = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!SecuritySettingsDeleteRequest}
   * @returns {!SecuritySettingsDeleteRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new SecuritySettingsDeleteRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new SecuritySettingsDeleteRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!SecuritySettingsDeleteRequest}
   * @param {!SecuritySettingsDeleteRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof SecuritySettingsDeleteRequest)) {
      throw new TypeError('Instance of SecuritySettingsDeleteRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!SecuritySettingsDeleteRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      SecuritySettingsId: ((this.SecuritySettingsId != null) ? this.SecuritySettingsId.toString() : null),
      SecuritySettingsName: ((this.SecuritySettingsName != null) ? this.SecuritySettingsName : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return SecuritySettingsDeleteRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!SecuritySettingsDeleteRequest} other Object value
   * @returns {!SecuritySettingsDeleteRequest} Created struct
   */
  static fromObject (other) {
    return new SecuritySettingsDeleteRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!SecuritySettingsDeleteRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return SecuritySettingsDeleteRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!SecuritySettingsDeleteRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6540
  }
}

export { SecuritySettingsDeleteRequest };

/**
 * Fast Binary Encoding SecuritySettingsDeleteRequest field model
 */
class FieldModelSecuritySettingsDeleteRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelConfigUpdateRequest(buffer, 4 + 4)
    this.SecuritySettingsId = new fbe.FieldModelOptional(new fbe.FieldModelUUID(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4), buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.SecuritySettingsName = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.SecuritySettingsId.fbeOffset + this.SecuritySettingsId.fbeSize), buffer, this.SecuritySettingsId.fbeOffset + this.SecuritySettingsId.fbeSize)
  }

  /**
   * Get the ConfigUpdateRequest field model
   * @this {!FieldModelSecuritySettingsDeleteRequest}
   * @returns {!FieldModelConfigUpdateRequest} ConfigUpdateRequest field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelSecuritySettingsDeleteRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelSecuritySettingsDeleteRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.SecuritySettingsId.fbeSize + this.SecuritySettingsName.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelSecuritySettingsDeleteRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.SecuritySettingsId.fbeExtra + this.SecuritySettingsName.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelSecuritySettingsDeleteRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelSecuritySettingsDeleteRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelSecuritySettingsDeleteRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6540
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelSecuritySettingsDeleteRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelSecuritySettingsDeleteRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.SecuritySettingsId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.SecuritySettingsId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SecuritySettingsId.fbeSize

    if ((fbeCurrentSize + this.SecuritySettingsName.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.SecuritySettingsName.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SecuritySettingsName.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelSecuritySettingsDeleteRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelSecuritySettingsDeleteRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelSecuritySettingsDeleteRequest}
   * @param {!SecuritySettingsDeleteRequest} fbeValue Default value, defaults is new SecuritySettingsDeleteRequest()
   * @returns {!SecuritySettingsDeleteRequest} SecuritySettingsDeleteRequest value
   */
  get (fbeValue = new SecuritySettingsDeleteRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelSecuritySettingsDeleteRequest}
   * @param {!SecuritySettingsDeleteRequest} fbeValue SecuritySettingsDeleteRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.SecuritySettingsId.fbeSize) <= fbeStructSize) {
      fbeValue.SecuritySettingsId = this.SecuritySettingsId.get()
    } else {
      fbeValue.SecuritySettingsId = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SecuritySettingsId.fbeSize

    if ((fbeCurrentSize + this.SecuritySettingsName.fbeSize) <= fbeStructSize) {
      fbeValue.SecuritySettingsName = this.SecuritySettingsName.get()
    } else {
      fbeValue.SecuritySettingsName = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.SecuritySettingsName.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelSecuritySettingsDeleteRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelSecuritySettingsDeleteRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelSecuritySettingsDeleteRequest}
   * @param {!SecuritySettingsDeleteRequest} fbeValue SecuritySettingsDeleteRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelSecuritySettingsDeleteRequest}
   * @param {SecuritySettingsDeleteRequest} fbeValue SecuritySettingsDeleteRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.SecuritySettingsId.set(fbeValue.SecuritySettingsId)
    this.SecuritySettingsName.set(fbeValue.SecuritySettingsName)
  }
}

export { FieldModelSecuritySettingsDeleteRequest };

/**
 * Fast Binary Encoding SecuritySettingsDeleteRequest model
 */
class SecuritySettingsDeleteRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelSecuritySettingsDeleteRequest(this.buffer, 4)
  }

  /**
   * Get the SecuritySettingsDeleteRequest model
   * @this {!SecuritySettingsDeleteRequestModel}
   * @returns {!FieldModelSecuritySettingsDeleteRequest} model SecuritySettingsDeleteRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!SecuritySettingsDeleteRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!SecuritySettingsDeleteRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return SecuritySettingsDeleteRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!SecuritySettingsDeleteRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelSecuritySettingsDeleteRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!SecuritySettingsDeleteRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!SecuritySettingsDeleteRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!SecuritySettingsDeleteRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!SecuritySettingsDeleteRequestModel}
   * @param {!SecuritySettingsDeleteRequest} value SecuritySettingsDeleteRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!SecuritySettingsDeleteRequestModel}
   * @param {!SecuritySettingsDeleteRequest} value SecuritySettingsDeleteRequest value, defaults is new SecuritySettingsDeleteRequest()
   * @return {!object} Deserialized SecuritySettingsDeleteRequest value and its size
   */
  deserialize (value = new SecuritySettingsDeleteRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new SecuritySettingsDeleteRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new SecuritySettingsDeleteRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!SecuritySettingsDeleteRequestModel}
   * @param {!number} prev Previous SecuritySettingsDeleteRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { SecuritySettingsDeleteRequestModel };
SecuritySettingsDeleteRequest.__has_response = true;
SecuritySettingsDeleteRequest.__response_class = SecuritySettingsDeleteResponse;
